import React, { useMemo, useState } from "react";

function startOfWeek(date) {
  const d = new Date(date);
  const day = d.getDay(); // 0=Sun, 1=Mon..6=Sat
  const diff = (day + 6) % 7; // Monday-start week
  d.setDate(d.getDate() - diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function fmtISODate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONTH_LABELS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

export default function CalendarView({ appts, services, onNotify, onNoShow }) {
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [noShowFor, setNoShowFor] = useState(null); // appt object being flagged
  const [smsWithNoShow, setSmsWithNoShow] = useState(true);

  const days = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + i);
      arr.push(d);
    }
    return arr;
  }, [weekStart]);

  const confirmedByDay = useMemo(() => {
    const map = {};
    const confirmedStatuses = new Set(["confirmed", "in_progress"]);
    appts
      .filter((a) => confirmedStatuses.has(a.status))
      .forEach((a) => {
        const key = a.preferred_date;
        if (!map[key]) map[key] = [];
        map[key].push(a);
      });
    Object.values(map).forEach((list) => list.sort((x, y) => (x.preferred_time || "").localeCompare(y.preferred_time || "")));
    return map;
  }, [appts]);

  const titleOne = (id) => services.find((s) => s.id === id)?.title || id;
  const titleFor = (a) => {
    if (a && typeof a === "object") {
      const ids = a.services?.length ? a.services : [a.service];
      return ids.map(titleOne).join(" + ");
    }
    return titleOne(a);
  };
  const rangeLabel = `${days[0].getDate()} ${MONTH_LABELS[days[0].getMonth()]} — ${days[6].getDate()} ${MONTH_LABELS[days[6].getMonth()]} ${days[6].getFullYear()}`;
  const today = fmtISODate(new Date());

  return (
    <div data-testid="admin-calendar" className="space-y-5">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-brand-red">Week view</div>
          <h3 className="mt-1 font-display text-2xl uppercase tracking-tight text-white">{rangeLabel}</h3>
          <p className="text-xs text-zinc-500 mt-1">Confirmed &amp; in-progress bookings only.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="cal-prev-week"
            onClick={() => { const d = new Date(weekStart); d.setDate(d.getDate() - 7); setWeekStart(d); }}
            className="px-3 py-2 border border-zinc-800 text-white text-[11px] font-mono uppercase tracking-widest hover:border-brand-red hover:text-brand-red"
          >
            ← Prev
          </button>
          <button
            data-testid="cal-today"
            onClick={() => setWeekStart(startOfWeek(new Date()))}
            className="px-3 py-2 border border-zinc-800 text-white text-[11px] font-mono uppercase tracking-widest hover:border-brand-red hover:text-brand-red"
          >
            Today
          </button>
          <button
            data-testid="cal-next-week"
            onClick={() => { const d = new Date(weekStart); d.setDate(d.getDate() + 7); setWeekStart(d); }}
            className="px-3 py-2 border border-zinc-800 text-white text-[11px] font-mono uppercase tracking-widest hover:border-brand-red hover:text-brand-red"
          >
            Next →
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-7 gap-px bg-zinc-900 border border-zinc-900">
        {days.map((d, i) => {
          const iso = fmtISODate(d);
          const list = confirmedByDay[iso] || [];
          const isToday = iso === today;
          return (
            <div
              key={iso}
              data-testid={`cal-day-${iso}`}
              className={`bg-zinc-950 min-h-[260px] p-3 ${isToday ? "ring-1 ring-brand-red ring-inset" : ""}`}
            >
              <div className="flex items-baseline justify-between mb-3">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{WEEKDAY_LABELS[i]}</div>
                  <div className={`font-display text-2xl ${isToday ? "text-brand-red" : "text-white"}`}>{d.getDate()}</div>
                </div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-600">
                  {list.length ? `${list.length} appt${list.length > 1 ? "s" : ""}` : "—"}
                </div>
              </div>
              <div className="space-y-2">
                {list.map((a) => (
                  <div
                    key={a.id}
                    data-testid={`cal-appt-${a.id}`}
                    className={`border p-2.5 text-xs ${a.status === "in_progress" ? "border-orange-700 bg-orange-950/20" : "border-brand-red/40 bg-brand-red/5"}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono text-brand-red text-[11px]">{a.preferred_time}</span>
                      <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">
                        {a.status === "in_progress" ? "IN PROG" : "CONF"}
                      </span>
                    </div>
                    <div className="mt-1 text-white font-medium truncate" title={a.user_name}>{a.user_name}</div>
                    <div className="text-zinc-400 text-[11px] truncate" title={titleFor(a)}>{titleFor(a)}</div>
                    <div className="text-zinc-500 text-[10px] truncate">{a.vehicle_year} {a.vehicle_make} {a.vehicle_model}</div>
                    {a.user_phone && (
                      <a href={`tel:${a.user_phone}`} className="mt-1 block text-[10px] font-mono text-zinc-400 hover:text-brand-red truncate">
                        {a.user_phone}
                      </a>
                    )}
                    {!a.customer_notified_at && (
                      <button
                        data-testid={`cal-notify-${a.id}`}
                        onClick={() => onNotify(a.id)}
                        className="mt-2 w-full px-2 py-1 bg-brand-red text-white font-mono text-[9px] uppercase tracking-widest hover:bg-[#B91C2B]"
                      >
                        Notify customer
                      </button>
                    )}
                    <button
                      data-testid={`cal-noshow-${a.id}`}
                      onClick={() => { setNoShowFor(a); setSmsWithNoShow(true); }}
                      className="mt-1.5 w-full px-2 py-1 border border-zinc-700 text-zinc-300 font-mono text-[9px] uppercase tracking-widest hover:border-red-700 hover:text-red-400"
                    >
                      Mark no-show
                    </button>
                  </div>
                ))}
                {list.length === 0 && (
                  <div className="text-zinc-700 text-xs font-mono">No bookings</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {noShowFor && (
        <div
          data-testid="noshow-modal"
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm grid place-items-center p-6"
          onClick={() => setNoShowFor(null)}
        >
          <div
            className="bg-zinc-950 border border-zinc-800 max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-brand-red">Mark as no-show</div>
            <h4 className="mt-2 font-display text-2xl uppercase tracking-tight text-white">
              {noShowFor.user_name}
            </h4>
            <p className="mt-1 text-xs text-zinc-500 font-mono">
              {noShowFor.preferred_date} at {noShowFor.preferred_time} · {titleFor(noShowFor)}
            </p>
            <p className="mt-4 text-sm text-zinc-300">
              This will mark the booking as <span className="text-red-400 font-semibold">no-show</span> and free the slot.
            </p>
            <label className="mt-4 flex items-start gap-2.5 cursor-pointer select-none" data-testid="noshow-sms-toggle">
              <input
                type="checkbox"
                className="mt-0.5 accent-brand-red h-4 w-4"
                checked={smsWithNoShow}
                onChange={(e) => setSmsWithNoShow(e.target.checked)}
              />
              <span className="text-sm text-zinc-300">
                Also send the customer an SMS asking them to reschedule
                <span className="block text-[11px] text-zinc-500 mt-0.5">
                  (Only sent if their phone starts with 04.)
                </span>
              </span>
            </label>
            <div className="mt-6 flex gap-2 justify-end">
              <button
                onClick={() => setNoShowFor(null)}
                className="px-4 py-2 border border-zinc-800 text-zinc-300 font-mono text-[11px] uppercase tracking-widest hover:border-zinc-600 hover:text-white"
              >
                Cancel
              </button>
              <button
                data-testid="noshow-confirm-btn"
                onClick={() => {
                  onNoShow(noShowFor.id, smsWithNoShow);
                  setNoShowFor(null);
                }}
                className="px-4 py-2 bg-red-700 text-white font-mono text-[11px] uppercase tracking-widest hover:bg-red-800"
              >
                Confirm no-show
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
